import { uniq } from 'lodash';
import pluralize from 'pluralize';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { Icon } from '@/components/icon';
import { CarrierLogoStackNames } from '@/components/modules/carrier/logo-stack-names';
import { EmissionsIndicator } from '@/components/modules/emissions/indicator';
import { RatesMarketRateIndicator } from '@/components/modules/rates/market-rate-indicator';
import { RouteBarMini } from '@/components/modules/route/bar-mini';
import { RouteDirections } from '@/components/modules/route/directions';
import { Text } from '@/components/text';
import { Tooltip } from '@/components/tooltip';
import { EMISSIONS_DATA_SOURCES, MODE_AIR } from '@/lib/constants';
import { RouteModel } from '@/lib/models/route/types';
import { getTotalRouteModeEmissions } from '@/lib/models/route/utils/get-route-total-emissions';
import { getLocationByIdState } from '@/state/locations';
import { routesStatsState } from '@/state/routes';
import { XenetaRatesFieldApiType } from '@/types/api-types';
import { getPreferredIdentifier } from '@/utils/helpers';
import { shortenLocationName } from '@/utils/locations';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { RouteTransitDurationIndicator } from '../transit-duration-indicator';

export interface RouteSearchResultRowProps {
  route: RouteModel;
  onRouteClick: (route: RouteModel) => void;
  onDetailsClick: (route: RouteModel) => void;
  index: number;
  shouldDrawSeparator?: boolean;
  isHero?: boolean;
  isLocked?: boolean;
  ratesAreLocked?: boolean;
  canSaveRates?: boolean;
  rateFields?: XenetaRatesFieldApiType;
  isActive?: boolean;
  testId?: string;
}

const RouteSearchResultRow = ({
  route,
  shouldDrawSeparator = false,
  index,
  isHero,
  isLocked,
  ratesAreLocked,
  canSaveRates,
  rateFields,
  onRouteClick,
  onDetailsClick,
  isActive = false,
  testId,
}: RouteSearchResultRowProps) => {
  const { end, endTransfer, fromId, mainOptions, primaryMode, start, startTransfer, toId } = route;

  const [isOpen, setIsOpen] = useState<boolean>(isActive);
  const fromPort = useRecoilValue(getLocationByIdState(fromId));
  const toPort = useRecoilValue(getLocationByIdState(toId));
  const routeStatsOverview = useRecoilValue(routesStatsState);

  const primaryOption = mainOptions[0];
  const alternatives = mainOptions.length;
  const departurePortId = getPreferredIdentifier(fromPort?.identifiers, true)?.value;
  const arrivalPortId = getPreferredIdentifier(toPort?.identifiers, true)?.value;
  const isFullWidthBar = !departurePortId && !arrivalPortId;
  const emissionsStats = routeStatsOverview?.emissions.e2e[primaryMode];
  const routeEmissions = getTotalRouteModeEmissions([primaryOption.emissions, start?.emissions, end?.emissions]);

  const freighterAvailable =
    route.primaryMode === MODE_AIR && route.mainOptions.some((option) => option.hasEndToEndFreighterAvailable);

  const handleRouteClick = () => {
    setIsOpen(!isOpen);
    onRouteClick(route);

    if (!isOpen) {
      // Analytics Event
      trackUser.event('Open Route Card', {
        origin: departurePortId,
        destination: arrivalPortId,
        index: index + 1,
      });
    }
  };

  const handleRouteKeyPress = (e: React.KeyboardEvent) => {
    if (['Enter', ' '].includes(e.key)) {
      handleRouteClick();
    }
  };

  const handleDetailsClick = () => {
    onDetailsClick(route);
  };

  const {
    base,
    card,
    grid,
    gridBox,
    locationGridBox,
    moreDetailToggle,
    portCodeText,
    routeBarWrapper,
    subText,
    freighterTooltip,
  } = styles({
    isActive,
    isOpen,
    shouldDrawSeparator,
    isHero,
  });

  return (
    <div className={base()} data-cy={testId}>
      <div className={card()}>
        <div className={grid()} role="button" tabIndex={0} onClick={handleRouteClick} onKeyUp={handleRouteKeyPress}>
          <div className={locationGridBox()}>
            <div>
              <Text className={portCodeText()}>{departurePortId || ''}</Text>
              <Text className={subText({ isFullWidthBar })}>{fromPort ? shortenLocationName(fromPort.name) : ''}</Text>
            </div>
            <div className="ml-2 text-right">
              <Text className={portCodeText()}>{arrivalPortId || ''}</Text>
              <Text className={subText({ isFullWidthBar })}>{toPort ? shortenLocationName(toPort.name) : ''}</Text>
            </div>
            <div className={routeBarWrapper({ isFullWidthBar })}>
              <RouteBarMini
                end={end}
                fullWidth={isFullWidthBar}
                mainOptions={mainOptions}
                primaryMode={primaryMode}
                start={start}
              />
            </div>
          </div>
          <div className={gridBox({ className: 'hidden md:flex' })}>
            <RouteTransitDurationIndicator route={route} />
          </div>
          <div className={gridBox({ className: 'hidden xl:flex' })}>
            <RatesMarketRateIndicator
              mode={primaryMode}
              fromId={fromId}
              toId={toId}
              fromPortName={departurePortId}
              toPortName={arrivalPortId}
              rateFields={rateFields}
              upgradeRequired={ratesAreLocked}
              isNotSignedIn={isLocked}
              canSaveRates={canSaveRates}
            />
          </div>
          <div className={gridBox({ className: 'hidden md:flex' })}>
            <EmissionsIndicator
              mode={primaryMode}
              value={routeEmissions?.total}
              unit={routeEmissions?.unit}
              comparison={emissionsStats}
              modeBreakdown={routeEmissions}
              isLocked={isLocked}
              source={{ type: EMISSIONS_DATA_SOURCES.ROUTE, data: route }}
              canOpenInCalculator
            />
          </div>
          <div className={gridBox({ className: 'relative hidden xl:flex' })}>
            <CarrierLogoStackNames
              carrierIds={uniq(
                route.mainOptions.reduce((acc, option) => [...acc, ...option.carrierIds], [] as string[]),
              )}
              limit={6}
              isLocked={isLocked}
              mode={primaryMode}
              customLabel={`${alternatives} ${pluralize('service', alternatives)}`}
            />
            {freighterAvailable && (
              <Tooltip content="Freighter available">
                <span className={freighterTooltip()}>F</span>
              </Tooltip>
            )}
          </div>
          <div className={moreDetailToggle()} data-testid="toggle-details">
            <Icon name="down" size="20px" color="grey.500" transform={isOpen ? 'rotate(-180deg)' : undefined} />
          </div>
        </div>
        {isOpen && (
          <RouteDirections
            arrivalPortId={toId}
            departurePortId={fromId}
            end={end}
            endTransfer={endTransfer}
            isLocked={isLocked}
            onClick={handleDetailsClick}
            routeMode={primaryMode}
            segments={primaryOption.segments}
            start={start}
            startTransfer={startTransfer}
          />
        )}
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative gap-y-2 md:-ml-2 md:-mr-2',
    card: "group relative overflow-hidden border border-x-0 border-y border-grey-200 bg-white after:absolute after:bottom-0 after:left-0 after:top-0 after:hidden after:w-1 after:rounded-bl-md after:rounded-tl-md after:bg-lightBlue-500 after:content-[''] hover:border-grey-400 hover:shadow-card md:rounded-md md:border",
    grid: 'grid grid-cols-[1fr_54px] px-1 py-3.5 leading-[14px] md:grid-cols-[var(--route-card-grid-cols-md)] xl:grid-cols-[var(--route-card-grid-cols-xl)]',
    gridBox: 'flex flex-col justify-center overflow-auto border-l border-grey-200 pb-2 pl-4 pr-2 pt-2',
    locationGridBox: 'relative grid grid-cols-2 items-center gap-x-6 px-4 py-2',
    moreDetailToggle:
      '-my-4 -mr-1 ml-0 grid cursor-pointer place-items-center border-l border-l-grey-200 bg-grey-50 group-hover:border-l-grey-300 group-hover:bg-grey-100 group-hover:[&_svg]:!fill-lightBlue-600',
    portCodeText: 'm-0 block text-baseSm font-semiBold tracking-wide text-text-primary',
    primaryText: 'm-0 block text-baseSm text-text-primary',
    subText: 'mb-0 ml-0 mr-0 mt-1.5 overflow-hidden overflow-ellipsis whitespace-nowrap text-sm text-text-secondary',
    routeBarWrapper: 'absolute left-1/2 top-1.5 -translate-x-1/2',
    freighterTooltip:
      'absolute right-3 top-0 grid h-4 w-4 place-items-center rounded-full bg-lightBlue-100 text-sm text-lightBlue-800',
    alertWrapper: 'absolute right-2 top-2 grid size-4 place-items-center',
  },
  variants: {
    isFullWidthBar: {
      true: {
        routeBarWrapper: 'left-3 right-3 translate-x-0',
        subText: 'mt-[18px]',
      },
    },
    shouldDrawSeparator: {
      true: {
        base: "!mt-3 border-t border-dotted border-t-grey-300 after:absolute after:-top-1.5 after:left-1 after:right-1 after:content-['']",
      },
    },
    isOpen: {
      true: {
        base: '!mb-4',
      },
    },
    isActive: {
      true: {
        base: '!border-grey-400 shadow-card',
      },
    },
    isHero: {
      true: {
        card: 'border-grey-400',
        portCodeText: 'text-base font-semiBold',
      },
    },
  },
});

export { RouteSearchResultRow };
